import React, { useCallback, useEffect, useRef } from "react"
import { Layout, theme, Divider, Menu } from "antd"
import hotkeys from "hotkeys-js"
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom"
import {
  SwapOutlined,
  HomeOutlined,
  TeamOutlined,
  UserOutlined,
  TableOutlined,
  FolderOutlined,
  LogoutOutlined,
  WalletOutlined,
  HistoryOutlined,
  FastForwardOutlined
} from "@ant-design/icons"

import { W0, SIDEBAR_WIDTH } from "@components"
import { HotKey } from "@components/Text"
import { OrganizationsModal } from "@components/Organization"
import { GoToInvestmentModal } from "@components/Investment"

import { SidebarDropdown } from "../../../backstage"

const { Sider } = Layout

const LABEL_USERS = "Users"
const LABEL_GROUPS = "Groups"
const LABEL_SIGNOUT = "Sign Out"
const LABEL_REPORTS = "Reports"
const LABEL_SWITCH_TO = "Switch To"
const LABEL_SWITCH_TO_HOTKEY = "ctrl + o"
const LABEL_CUSTOMERS = "ACH Accounts"
const LABEL_ACTIVITIES = "Activities"
const LABEL_ORGANIZATIONS = "Organizations"
const LABEL_MY_PROFILE = "My Profile"
const LABEL_GO_TO_INVESTMENT = "Go To Investment"
const LABEL_GO_TO_INVESTMENT_HOTKEY = "ctrl + j"


const Sidebar = () => {
  const organizationsModal = useRef({})
  const goToInvestmentModal = useRef({})

  const { token } = theme.useToken()

  const openOrganizationsModal = useCallback(() => {
    organizationsModal.current.open()
  }, [])

  const openGoToInvestmentModal = useCallback(() => {
    goToInvestmentModal.current.open()
  }, [])

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const dropdownItems = [
    {
      key: "investment",
      icon: <FastForwardOutlined />,
      label: (
        <HotKey hotKey={LABEL_GO_TO_INVESTMENT_HOTKEY}>
          <span>{LABEL_GO_TO_INVESTMENT}</span>
        </HotKey>
      ),
      onClick: openGoToInvestmentModal,
    },
    {
      type: 'divider',
    },
    {
      key: "switch",
      icon: <SwapOutlined />,
      label: (
        <HotKey hotKey={LABEL_SWITCH_TO_HOTKEY}>
          <span>{LABEL_SWITCH_TO}</span>
        </HotKey>
      ),
      onClick: openOrganizationsModal,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: (
        <NavLink to="/logout/backstage">
          {LABEL_SIGNOUT}
        </NavLink>
      )
    }
  ]

  useEffect(() => {
    hotkeys("ctrl+j", (event) => {
      event.preventDefault()
      openGoToInvestmentModal()
    })

    hotkeys("ctrl+o", (event) => {
      event.preventDefault()
      openOrganizationsModal()
    })

    return () => {
      hotkeys.unbind("ctrl+j")
      hotkeys.unbind("ctrl+o")
    }
  }, [openGoToInvestmentModal, openOrganizationsModal])

  const items = [
    {
      key: "/backstage/organizations",
      icon: <HomeOutlined />,
      label: <Link to="/backstage/organizations">{LABEL_ORGANIZATIONS}</Link>,
    },
    {
      key: "/backstage/customers",
      icon: <WalletOutlined />,
      label: <Link to="/backstage/customers">{LABEL_CUSTOMERS}</Link>,
    },
    {
      key: "/backstage/groups",
      icon: <FolderOutlined />,
      label: <Link to="/backstage/groups">{LABEL_GROUPS}</Link>,
    },
    {
      key: "/backstage/users",
      icon: <TeamOutlined />,
      label: <Link to="/backstage/users">{LABEL_USERS}</Link>,
    },
    {
      key: "/backstage/activities",
      icon: <HistoryOutlined />,
      label: <Link to="/backstage/activities">{LABEL_ACTIVITIES}</Link>,
    },
    {
      key: "/backstage/reports",
      icon: <TableOutlined />,
      label: <Link to="/backstage/reports">{LABEL_REPORTS}</Link>,
    },
    {
      key: "/backstage/my-profile",
      icon: <UserOutlined />,
      label: <Link to="/backstage/my-profile">{LABEL_MY_PROFILE}</Link>,
    },
  ]

  const selectedKeys = items
    .map(item => item.key)
    .filter(key => pathname.startsWith(`${key}`))

  const { colorBorderSecondary, colorBgContainer } = token

  const style = {
    top: 0,
    left: 0,
    bottom: 0,
    overflow: 'auto',
    position: 'fixed',
    background: colorBgContainer,
    borderRight: `1px solid ${colorBorderSecondary}`,
  }

  return (
    <Sider
      width={SIDEBAR_WIDTH}
      style={style}
    >
      <SidebarDropdown
        items={dropdownItems}
      />

      <Divider style={{ margin: 0 }} />

      <Menu
        style={{ border: "none" }}
        items={items}
        onClick={({ key }) => navigate(key)}
        selectedKeys={selectedKeys}
        inlineIndent={W0 + 4}
      />

      <OrganizationsModal
        modal={organizationsModal}
      />

      <GoToInvestmentModal
        modal={goToInvestmentModal}
      />
    </Sider>
  )
}

export default Sidebar
